import { get, ref, runTransaction, update } from "firebase/database";
import { db } from "../firebaseConfig";
import {
  normalizeRecordingXpMode,
  resolveRecordingKmPerXp,
} from "./recordingXpConfig.js";

const DAILY_LOGIN_XP = 1;
const EVENT_PAYMENT_XP = 5;
const MAX_AWARDED_KEYS = 400;
const KEEP_AWARDED_KEYS = 250;

function isMobileNativeRuntime() {
  try {
    const cap = globalThis?.window?.Capacitor;
    const isNative = !!cap?.isNativePlatform?.();
    const platform = String(cap?.getPlatform?.() || "").toLowerCase();
    const userAgent = String(globalThis?.window?.navigator?.userAgent || "");
    const hasIOSDirectBridge =
      typeof globalThis?.window?.webkit?.messageHandlers?.nativeBridge?.postMessage ===
      "function";
    const isIOSDirectContainer =
      hasIOSDirectBridge && /iPad|iPhone|iPod/i.test(userAgent);
    return (isNative && (platform === "ios" || platform === "android")) ||
      isIOSDirectContainer;
  } catch {
    return false;
  }
}

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function localDayKey(ts = Date.now()) {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function trimAwardedMap(mapObj) {
  const entries = Object.entries(mapObj || {});
  if (entries.length <= MAX_AWARDED_KEYS) return mapObj || {};
  const entryTs = (value) =>
    value && typeof value === "object"
      ? toFiniteNumber(value.at ?? value.ts, 0)
      : toFiniteNumber(value, 0);
  entries.sort((a, b) => entryTs(b[1]) - entryTs(a[1]));
  const trimmed = entries.slice(0, KEEP_AWARDED_KEYS);
  return Object.fromEntries(trimmed);
}

export async function awardDailyLoginXp(uid) {
  if (!isMobileNativeRuntime()) {
    return { ok: false, grantedXp: 0, reason: "runtime_not_mobile_native" };
  }
  const safeUid = String(uid || "").trim();
  if (!safeUid) return { ok: false, grantedXp: 0, reason: "missing_uid" };

  const now = Date.now();
  const dateKey = localDayKey(now);
  let grantedXp = 0;

  const tx = await runTransaction(ref(db, `gamification/users/${safeUid}`), (current) => {
    const next = current && typeof current === "object" ? { ...current } : {};
    const loginNode =
      next.dailyLoginXp && typeof next.dailyLoginXp === "object"
        ? { ...next.dailyLoginXp }
        : {};
    if (String(loginNode.lastDateKey || "") === dateKey) {
      return;
    }
    const prevXp = Math.max(0, Math.floor(toFiniteNumber(next.xpTotal, 0)));
    const addXp = DAILY_LOGIN_XP;
    next.xpTotal = prevXp + addXp;
    next.dailyLoginXp = {
      ...loginNode,
      lastDateKey: dateKey,
      lastGrantedAt: now,
      grantedXp: addXp,
    };
    next.updatedAt = now;
    grantedXp = addXp;
    return next;
  });

  return {
    ok: !!tx?.committed,
    grantedXp: tx?.committed ? grantedXp : 0,
    dateKey,
  };
}

export async function awardRecordingDistanceXp(uid, distanceKm, entryId = null, options = {}) {
  if (!isMobileNativeRuntime()) {
    return { ok: false, grantedXp: 0, reason: "runtime_not_mobile_native" };
  }
  const safeUid = String(uid || "").trim();
  if (!safeUid) return { ok: false, grantedXp: 0, reason: "missing_uid" };

  const kmRaw = toFiniteNumber(distanceKm, 0);
  if (!(kmRaw > 0)) return { ok: false, grantedXp: 0, reason: "missing_distance" };

  const now = Date.now();
  const safeEntryId = String(entryId || "").trim() || null;
  const ledgerKey = safeEntryId
    ? safeEntryId.replace(/[.#$\[\]/]/g, "_")
    : null;
  const km = Math.max(0, kmRaw);
  const elevationGainM = Math.max(0, Math.round(toFiniteNumber(options?.elevationGainM, 0)));
  const recordedAtRaw = Number(options?.recordedAt || 0);
  const recordedAt = Number.isFinite(recordedAtRaw) && recordedAtRaw > 0
    ? recordedAtRaw
    : now;
  const recordedDate = new Date(recordedAt);
  const monthKey = `${recordedDate.getFullYear()}-${String(recordedDate.getMonth() + 1).padStart(2, "0")}`;
  const requestedMode = String(options?.activityMode || "walk").trim().toLowerCase();
  const activityMode = ["walk", "bike", "both"].includes(requestedMode)
    ? requestedMode
    : "walk";
  const recordingXpMode = normalizeRecordingXpMode(activityMode);
  let recordingXpConfig = null;
  try {
    const configSnapshot = await get(ref(db, "gamification/config/recordingDistanceXp"));
    recordingXpConfig = configSnapshot.val() || null;
  } catch {
    recordingXpConfig = null;
  }
  const kmPerXp = resolveRecordingKmPerXp(recordingXpConfig, recordingXpMode);
  let grantedXp = 0;
  let remainderKm = 0;

  const tx = await runTransaction(ref(db, `gamification/users/${safeUid}`), (current) => {
    const next = current && typeof current === "object" ? { ...current } : {};
    const recNode =
      next.recordingXp && typeof next.recordingXp === "object"
        ? { ...next.recordingXp }
        : {};
    const awardedEntries =
      recNode.awardedEntries && typeof recNode.awardedEntries === "object"
        ? { ...recNode.awardedEntries }
        : {};

    const distanceNode =
      next.recordingDistanceRanking && typeof next.recordingDistanceRanking === "object"
        ? { ...next.recordingDistanceRanking }
        : {};
    const rankedEntries =
      distanceNode.awardedEntries && typeof distanceNode.awardedEntries === "object"
        ? { ...distanceNode.awardedEntries }
        : {};
    const xpAlreadyAwarded = !!(safeEntryId && awardedEntries[safeEntryId]);
    const distanceAlreadyAwarded = !!(ledgerKey && rankedEntries[ledgerKey]);
    const elevationAlreadyAwarded = !!(
      ledgerKey &&
      rankedEntries[ledgerKey] &&
      typeof rankedEntries[ledgerKey] === "object" &&
      Object.prototype.hasOwnProperty.call(rankedEntries[ledgerKey], "elevationGainM")
    );
    if (xpAlreadyAwarded && distanceAlreadyAwarded && elevationAlreadyAwarded) {
      return;
    }

    if (!xpAlreadyAwarded) {
      const remainderKmByMode =
        recNode.remainderKmByMode && typeof recNode.remainderKmByMode === "object"
          ? { ...recNode.remainderKmByMode }
          : {};
      const hasModeRemainder = Object.prototype.hasOwnProperty.call(
        remainderKmByMode,
        recordingXpMode
      );
      const legacyWalkRemainder =
        recordingXpMode === "walk" ? Math.max(0, toFiniteNumber(recNode.remainderKm, 0)) : 0;
      const prevRemainderKm = Math.max(
        0,
        toFiniteNumber(
          hasModeRemainder ? remainderKmByMode[recordingXpMode] : legacyWalkRemainder,
          0
        )
      );
      const totalKm = prevRemainderKm + km;
      const gain = Math.max(0, Math.floor(totalKm / kmPerXp));
      const nextRemainder = Number(
        Math.max(0, totalKm - gain * kmPerXp).toFixed(3)
      );
      if (safeEntryId) awardedEntries[safeEntryId] = now;
      const prevXp = Math.max(0, Math.floor(toFiniteNumber(next.xpTotal, 0)));
      if (gain > 0) {
        next.xpTotal = prevXp + gain;
        grantedXp = gain;
      }
      remainderKm = nextRemainder;
      remainderKmByMode[recordingXpMode] = nextRemainder;
      recNode.remainderKm = nextRemainder;
      recNode.remainderKmByMode = remainderKmByMode;
      recNode.lastDistanceKm = Number(km.toFixed(3));
      recNode.lastActivityMode = recordingXpMode;
      recNode.lastKmPerXp = kmPerXp;
      recNode.lastGrantedAt = now;
      recNode.lastGrantedXp = gain;
      recNode.awardedEntries = trimAwardedMap(awardedEntries);
      next.recordingXp = recNode;
    } else {
      remainderKm = Math.max(
        0,
        toFiniteNumber(
          recNode?.remainderKmByMode?.[recordingXpMode] ?? recNode.remainderKm,
          0
        )
      );
    }

    if (!distanceAlreadyAwarded || !elevationAlreadyAwarded) {
      const distanceIncrement = distanceAlreadyAwarded ? 0 : km;
      const elevationIncrement = elevationAlreadyAwarded ? 0 : elevationGainM;
      const byMode = next.monthlyStatsByMode && typeof next.monthlyStatsByMode === "object"
        ? { ...next.monthlyStatsByMode }
        : {};
      const modeMonths = byMode[activityMode] && typeof byMode[activityMode] === "object"
        ? { ...byMode[activityMode] }
        : {};
      const currentMode = modeMonths[monthKey] && typeof modeMonths[monthKey] === "object"
        ? { ...modeMonths[monthKey] }
        : {};
      currentMode.monthKey = monthKey;
      currentMode.activityMode = activityMode;
      currentMode.distanceKm = Number((Math.max(0, toFiniteNumber(currentMode.distanceKm, 0)) + distanceIncrement).toFixed(2));
      currentMode.elevationGainM = Math.round(
        Math.max(0, toFiniteNumber(currentMode.elevationGainM, 0)) + elevationIncrement
      );
      currentMode.updatedAt = now;
      modeMonths[monthKey] = currentMode;
      byMode[activityMode] = modeMonths;
      next.monthlyStatsByMode = byMode;

      if (activityMode === "walk") {
        const monthly = next.monthlyStats && typeof next.monthlyStats === "object"
          ? { ...next.monthlyStats }
          : {};
        const currentMonth = monthly[monthKey] && typeof monthly[monthKey] === "object"
          ? { ...monthly[monthKey] }
          : {};
        currentMonth.monthKey = monthKey;
        currentMonth.distanceKm = Number((Math.max(0, toFiniteNumber(currentMonth.distanceKm, 0)) + distanceIncrement).toFixed(2));
        currentMonth.elevationGainM = Math.round(
          Math.max(0, toFiniteNumber(currentMonth.elevationGainM, 0)) + elevationIncrement
        );
        currentMonth.updatedAt = now;
        monthly[monthKey] = currentMonth;
        next.monthlyStats = monthly;
      }

      if (ledgerKey) {
        rankedEntries[ledgerKey] = {
          ...(typeof rankedEntries[ledgerKey] === "object" ? rankedEntries[ledgerKey] : {}),
          at: now,
          distanceKm: Number(km.toFixed(3)),
          elevationGainM,
          monthKey,
          activityMode,
        };
      }
      distanceNode.awardedEntries = trimAwardedMap(rankedEntries);
      distanceNode.lastEntryId = safeEntryId;
      distanceNode.lastDistanceKm = Number(km.toFixed(3));
      distanceNode.lastAwardedAt = now;
      next.recordingDistanceRanking = distanceNode;
    }
    next.updatedAt = now;
    return next;
  });

  // O painel e o ranking público leem usersPublic. Espelhamos o resultado já
  // confirmado pela transação, sem voltar a calcular a partir do histórico local.
  if (tx?.committed) {
    const value = tx.snapshot?.val?.() || {};
    const modeStats = value?.monthlyStatsByMode?.[activityMode]?.[monthKey] || null;
    const legacyStats = activityMode === "walk" ? value?.monthlyStats?.[monthKey] || null : null;
    const publicUpdates = {};
    if (modeStats) {
      publicUpdates[`usersPublic/${safeUid}/gamification/monthlyStatsByMode/${activityMode}/${monthKey}`] = modeStats;
    }
    if (legacyStats) {
      publicUpdates[`usersPublic/${safeUid}/gamification/monthlyStats/${monthKey}`] = legacyStats;
    }
    publicUpdates[`usersPublic/${safeUid}/gamification/updatedAt`] = now;
    await update(ref(db), publicUpdates).catch(() => {});
  }

  return {
    ok: !!tx?.committed,
    grantedXp: tx?.committed ? grantedXp : 0,
    remainderKm,
    kmPerXp,
    recordingXpMode,
    distanceKm: km,
    elevationGainM,
  };
}

export async function awardEventPaymentXp(uid, eventId) {
  if (!isMobileNativeRuntime()) {
    return { ok: false, grantedXp: 0, reason: "runtime_not_mobile_native" };
  }
  const safeUid = String(uid || "").trim();
  const safeEventId = String(eventId || "").trim();
  if (!safeUid) return { ok: false, grantedXp: 0, reason: "missing_uid" };
  if (!safeEventId) return { ok: false, grantedXp: 0, reason: "missing_event" };

  const now = Date.now();
  let grantedXp = 0;

  const tx = await runTransaction(ref(db, `gamification/users/${safeUid}`), (current) => {
    const next = current && typeof current === "object" ? { ...current } : {};
    const eventNode =
      next.eventPaymentXp && typeof next.eventPaymentXp === "object"
        ? { ...next.eventPaymentXp }
        : {};
    const awardedByEvent =
      eventNode.awardedByEvent && typeof eventNode.awardedByEvent === "object"
        ? { ...eventNode.awardedByEvent }
        : {};

    if (awardedByEvent[safeEventId]) {
      return;
    }

    awardedByEvent[safeEventId] = now;
    eventNode.awardedByEvent = trimAwardedMap(awardedByEvent);
    eventNode.lastEventId = safeEventId;
    eventNode.lastGrantedAt = now;
    eventNode.lastGrantedXp = EVENT_PAYMENT_XP;
    next.eventPaymentXp = eventNode;

    const prevXp = Math.max(0, Math.floor(toFiniteNumber(next.xpTotal, 0)));
    next.xpTotal = prevXp + EVENT_PAYMENT_XP;
    next.updatedAt = now;
    grantedXp = EVENT_PAYMENT_XP;
    return next;
  });

  return {
    ok: !!tx?.committed,
    grantedXp: tx?.committed ? grantedXp : 0,
    eventId: safeEventId,
  };
}
